<template>
  <div class="top-overlay__wrapper overflow-hidden" :class="{ active }">
    <div
      class="box-border relative sm:px-4 px-2 py-[0.675rem] bg-white dark:bg-black border-b border-neutral-200 dark:border-neutral-600 top-overlay__inner"
    >
      <div class="flex justify-between space-x-4 my-auto">
        <NamiButton
          class="whitespace-nowrap"
          buttonType="primary"
          :icon="!solo ? IconChevronLeft : IconX"
          hideTextOnSmallScreen
          pill
          text
          :href="solo ? undefined : returnHref"
          @buttonClick="(!returnHref || solo) && emit('return')"
        >
          <div class="hidden sm:block">
            {{
              solo
                ? translate("reader.overlay.top.button.close")
                : translate("reader.overlay.top.button.return")
            }}
          </div>
        </NamiButton>

        <div
          class="text-center overflow-hidden whitespace-nowrap font-medium my-auto"
          ref="titleWrapper"
        >
          <div class="transition-transform" ref="titleText">
            {{ overlayText }}
          </div>
        </div>

        <div class="flex space-x-2 my-auto">
          <slot name="buttons">
            <NamiButton v-for="button of buttons" v-bind="button" />
          </slot>
          <NamiButton
            buttonType="secondary"
            :icon="IconMenu2"
            hideTextOnSmallScreen
            pill
            :text="!menuOpen"
            @buttonClick="menuOpen = !menuOpen"
          >
            {{ translate("reader.overlay.top.button.menu") }}
          </NamiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IconChevronLeft, IconMenu2, IconX } from "@tabler/icons-vue";
import type { Props as ButtonProps } from "~/types/button";

const nuxtApp = useNuxtApp();
const { t: translate } = useI18n();

const { solo, buttons = [] } = defineProps<{
  buttons?: ButtonProps[];
  overlayText?: string;
  // only applicable if solo != true
  returnHref?: string;

  active?: boolean;
  solo?: boolean;
}>();

const emit = defineEmits<{ (e: "return"): void }>();
const menuOpen = defineModel<boolean>("menuOpen", { required: true });

const titleAreaScrollTimer = ref<NodeJS.Timeout | null>(null);
const titleAreaScrollX = ref(0);
const titleWrapper = ref<HTMLDivElement>();
const titleText = ref<HTMLDivElement>();

onMounted(() => {
  if (!titleWrapper.value || !titleText.value) return;
  if (titleWrapper.value.scrollWidth - titleText.value.clientWidth <= 0) return;

  let overflowScrollEnd = 0;
  let overflowScrollStart = -20;

  titleAreaScrollTimer.value = setInterval(() => {
    if (titleAreaScrollX.value > titleWrapper.value!.scrollWidth) {
      if (overflowScrollEnd < 10) return overflowScrollEnd++;

      overflowScrollStart = -20;
      overflowScrollEnd = 0;
      titleAreaScrollX.value = 0;
      titleText.value!.style.transform = `translateX(0)`;
    }

    if (overflowScrollStart < 0) return overflowScrollStart++;

    titleText.value!.style.transform = `translateX(-${titleAreaScrollX.value}px)`;
    titleAreaScrollX.value += 2;
  }, 100);
});

onBeforeUnmount(() => {
  clearInterval(titleAreaScrollTimer.value!);
});
</script>

<style scoped lang="postcss">
.top-overlay__wrapper {
  & > .top-overlay__inner {
    transform: translateY(-100%);
    transition: transform 150ms ease-in-out;
  }

  &:hover > .top-overlay__inner {
    @apply canhover:transform-none;
  }

  &.active > .top-overlay__inner {
    transform: none;
  }
}
</style>
